import {DropdownMenu, IconButton, TimeAgo} from '@wandb/components';
import {NavLinkNoCrawl} from '@wandb/weave/common/util/links';
import {RunState} from '@wandb/weave/components/RunState';
import gql from 'graphql-tag';
import React from 'react';
import type {Selection} from 'react-aria-components';
import {type CellInfo, type Column} from 'react-table';

import {
  type RunsTableFragmentFragment,
  type RunTag,
} from '../generated/graphql';
import {type Graph} from '../types/graphql';
import {useLocalStorage} from '../util/localStorage';
import {runGroup} from '../util/urls';
import ImageIconHeader from './ImageIconHeader';
import WBReactTable, {makeColumnSortMethod} from './WBReactTable';

const COLUMN_PICKER_DEFS: ReadonlyArray<{id: string; label: string}> = [
  {id: 'name', label: 'Name'},
  {id: 'runId', label: 'Run ID'},
  {id: 'project', label: 'Project'},
  {id: 'state', label: 'State'},
  {id: 'createdAt', label: 'Created'},
  {id: 'user', label: 'User'},
];

const RUNS_TABLE_HIDDEN_COLUMNS_STORAGE_KEY = 'runs-table:hidden-columns';

const DEFAULT_HIDDEN_COLUMN_IDS: ReadonlyArray<string> = ['runId'];

interface RunsTableProps {
  data: Graph<RunsTableRun> | null;
  showUserColumn: boolean;
  /* Pulling the header out when displaying the skeleton loader */
  suppressHeader?: boolean;
  pageSize?: number | null;
  setRunNameSearchStr?: (searchStr: string) => void;
  onChangePageSize?: (pageSize: number) => void;
}

export interface RunsTableRun {
  id: string;
  name: string;
  displayName: string;
  state: string;
  createdAt: string;
  heartbeatAt: string;
  updatedAt: string;
  notes?: string;
  user: {
    id: string;
    username: string;
    name?: string;
    accountType?: string;
  };
  project: {
    id: string;
    name: string;
    entityName: string;
  };
  groupCounts?: number[];
  tags?: RunTag[];
}

export const runsTableFragment = gql`
  fragment RunsTableFragment on Run {
    id
    name
    displayName
    state
    createdAt
    heartbeatAt
    updatedAt
    notes
    user {
      id
      username
      name
      accountType
    }
    project {
      id
      name
      entityName
    }
    groupCounts
    tags: tagColors {
      id
      name
      colorIndex
    }
  }
`;

export function runsTableRunFromQueryData(
  data?: Graph<RunsTableFragmentFragment> | null
): Graph<RunsTableRun> {
  if (data == null) {
    return {edges: []};
  }
  return {
    edges:
      data.edges
        .filter(run => run.node.project != null)
        .map(run => ({
          node: {
            id: run.node.id,
            name: run.node.name ?? '',
            state: run.node.state ?? '',
            displayName: run.node.displayName ?? '',
            createdAt: run.node.createdAt.toString(),
            heartbeatAt: run.node.heartbeatAt?.toString() ?? '',
            updatedAt: run.node.updatedAt?.toString() ?? '',
            user: {
              id: run.node.user?.id ?? '',
              username: run.node.user?.username ?? '',
            },
            project: {
              id: run.node.project?.id ?? '',
              name: run.node.project?.name ?? '',
              entityName: run.node.project?.entityName ?? '',
            },
            notes: run.node.notes ?? undefined,
            groupCounts: run.node.groupCounts ?? [],
            tags: (run.node as any).tags ?? [],
          },
        })) ?? [],
  };
}

const RunsTable: React.FC<RunsTableProps> = ({
  data,
  showUserColumn,
  suppressHeader = false,
  pageSize,
  setRunNameSearchStr,
  onChangePageSize,
}) => {
  const [hiddenColumnIds, setHiddenColumnIds] = useLocalStorage<string[]>(
    RUNS_TABLE_HIDDEN_COLUMNS_STORAGE_KEY,
    [...DEFAULT_HIDDEN_COLUMN_IDS]
  );

  const availableColumnDefs = showUserColumn
    ? COLUMN_PICKER_DEFS
    : COLUMN_PICKER_DEFS.filter(c => c.id !== 'user');

  const handleColumnSelectionChange = (keys: Selection) => {
    const allIds = availableColumnDefs.map(c => c.id);
    const visible =
      keys === 'all'
        ? new Set(allIds)
        : new Set(Array.from(keys, k => String(k)));
    setHiddenColumnIds(allIds.filter(id => !visible.has(id)));
  };

  const nameColumn: Column = {
    // ReactTable's sorting will break if 2 columns
    // have the same `accessor` but not unique `id`s.
    // This column needs an `id` that differentiates it from the 'project' column below.
    id: `name`,
    Header: 'Name',
    accessor: 'run',
    sortMethod: makeColumnSortMethod(run => run.displayName.toLowerCase()),
    Cell: (cellInfo: CellInfo) => {
      const run: RunsTableRun = cellInfo.value;
      const project = run.project;
      const groupCounts = run.groupCounts || [];

      if (groupCounts.length > 0) {
        return (
          <NavLinkNoCrawl
            to={runGroup({
              entityName: project.entityName,
              projectName: project.name,
              name: run.name,
            })}>
            <div>
              {run.displayName}{' '}
              <span style={{color: 'light-dark(gray, var(--moon-500))'}}>
                {' ('}
                {groupCounts[0]}
                {groupCounts[0] === 1 ? ' run)' : ' runs)'}
              </span>
            </div>
          </NavLinkNoCrawl>
        );
      }
      return (
        <NavLinkNoCrawl
          to={`/${run.project.entityName}/${run.project.name}/runs/${run.name}`}>
          {run.displayName}
        </NavLinkNoCrawl>
      );
    },
  };
  const projectColumn: Column = {
    // ReactTable's sorting will break if 2 columns
    // have the same `accessor` but not unique `id`s.
    // This column needs an `id` that differentiates it from the 'name' column above.
    id: `project`,
    Header: 'Project',
    accessor: 'run',
    sortMethod: makeColumnSortMethod(run => run.project.name.toLowerCase()),
    Cell: (cellInfo: CellInfo) => {
      const run: RunsTableRun = cellInfo.value;
      const project = run.project;
      return (
        <NavLinkNoCrawl to={`/${project.entityName}/${project.name}`}>
          {project.name}
        </NavLinkNoCrawl>
      );
    },
  };
  const userColumn: Column = {
    id: 'user',
    Header: 'User',
    accessor: 'run.user.username',
    Cell: (cellInfo: CellInfo) => {
      const username = cellInfo.value;
      return <NavLinkNoCrawl to={`/${username}`}>{username}</NavLinkNoCrawl>;
    },
  };
  const stateColumn: Column = {
    id: 'state',
    Header: 'State',
    accessor: 'state',
    Cell: (cellInfo: CellInfo) => <RunState value={cellInfo.value} />,
  };
  const createdColumn: Column = {
    id: 'createdAt',
    Header: 'Created',
    accessor: 'createdAt',
    sortMethod: makeColumnSortMethod((val: string) => {
      const d = new Date(val);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    }),
    Cell: (cellInfo: CellInfo) => <TimeAgo dateTime={cellInfo.value} />,
  };
  const runIdColumn: Column = {
    id: 'runId',
    Header: 'Run ID',
    accessor: 'run.name',
    sortMethod: makeColumnSortMethod((name: string) => name.toLowerCase()),
    Cell: (cellInfo: CellInfo) => (
      <span className="font-mono text-sm">{cellInfo.value}</span>
    ),
  };
  const allColumns = [
    nameColumn,
    runIdColumn,
    projectColumn,
    stateColumn,
    createdColumn,
  ];
  if (showUserColumn) {
    allColumns.push(userColumn);
  }
  const columns = allColumns.filter(
    c => !hiddenColumnIds.includes(c.id as string)
  );

  const visibleColumnIds = availableColumnDefs
    .filter(c => !hiddenColumnIds.includes(c.id))
    .map(c => c.id);

  const columnPicker = (
    <div className="mr-12 flex items-center">
      <DropdownMenu.Trigger>
        <IconButton
          size="sm"
          variant="ghost"
          icon="column"
          aria-label="Manage columns"
        />
        <DropdownMenu.Content
          selectionMode="multiple"
          selectedKeys={visibleColumnIds}
          onSelectionChange={handleColumnSelectionChange}>
          {availableColumnDefs.map(col => (
            <DropdownMenu.Item key={col.id} id={col.id}>
              <DropdownMenu.ItemLabel>{col.label}</DropdownMenu.ItemLabel>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Trigger>
    </div>
  );

  // runs *should* always have a project, but sometimes runs aren't deleted
  // when a project is -- this guards against that case
  const runs = (data?.edges || [])
    .filter(n => !!n.node.project)
    .map(n => ({
      searchString: `${n.node.displayName}${n.node.project.name}${n.node.name}`,
      row: {
        searchString: `${n.node.displayName}${n.node.project.name}${n.node.name}`,
        run: n.node,
        state: n.node.state,
        createdAt: n.node.createdAt,
      },
    }));

  return (
    <>
      {!suppressHeader && <ImageIconHeader icon="run" text="Runs" />}
      <WBReactTable
        data={runs}
        columns={columns}
        extraActions={columnPicker}
        pageSize={pageSize ?? 10}
        onChangePageSize={onChangePageSize}
        onSearch={setRunNameSearchStr}
        searchPlaceholder="Search run name or id"
      />
    </>
  );
};

export default RunsTable;
